import { useMapsLibrary } from "@vis.gl/react-google-maps";
import { useCallback, useState } from "react";
import { toast } from "sonner";
import { create } from "zustand";

import { neverthrowize } from "@/lib/data/call-server-api";

interface LocationStore {
  storedLocation: string | null;
  setStoredLocation: (location: string) => void;
}

const useLocationStore = create<LocationStore>((set) => ({
  storedLocation: null,
  setStoredLocation: (location) => set({ storedLocation: location }),
}));

const getCurrentPosition = (): Promise<GeolocationPosition> => {
  return new Promise((resolve, reject) => {
    if (!navigator.geolocation) {
      reject(new Error("Geolocation is not supported"));
      return;
    }

    navigator.geolocation.getCurrentPosition(resolve, reject, {
      enableHighAccuracy: true,
      timeout: 10000,
      maximumAge: 0,
    });
  });
};

export default function useCurrentLocation() {
  const geocodingLib = useMapsLibrary("geocoding");
  const [isLoading, setIsLoading] = useState(false);
  const { storedLocation, setStoredLocation } = useLocationStore();

  const resolveCoordinates = useCallback(
    async (lat: number, lng: number): Promise<string> => {
      if (!geocodingLib) {
        throw new Error("Geocoding library not loaded");
      }

      const geocoder = new geocodingLib.Geocoder();
      const response = await geocoder.geocode({ location: { lat, lng } });

      if (!response.results[0]) {
        throw new Error("No results found");
      }

      return response.results[0].formatted_address;
    },
    [geocodingLib],
  );

  const getCurrentLocation = useCallback(async (): Promise<string | null> => {
    if (storedLocation) {
      return storedLocation;
    }

    setIsLoading(true);

    const result = await neverthrowize([getCurrentPosition()]).andThen(
      ([position]) =>
        neverthrowize([
          resolveCoordinates(
            position.coords.latitude,
            position.coords.longitude,
          ),
        ]),
    );

    setIsLoading(false);

    return result.match(
      ([address]) => {
        setStoredLocation(address);
        return address;
      },
      () => {
        toast.error(
          "We couldn't resolve your location, please check permissions or try again later.",
        );
        return null;
      },
    );
  }, [resolveCoordinates, storedLocation, setStoredLocation]);

  return {
    getCurrentLocation,
    isLoading,
  };
}
